import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    Alert,
    CircularProgress,
    Autocomplete,
    useTheme,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { organizationApi, Organization } from '../../utils/organizationApi';
import { extraCreditsApi, AddExtraCreditsRequest } from '../../utils/extraCreditsApi';
import toast from 'react-hot-toast';

interface AddExtraCreditsModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

const AddExtraCreditsModal: React.FC<AddExtraCreditsModalProps> = ({
    open,
    onClose,
    onSuccess,
}) => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);
    const [amount, setAmount] = useState('');
    const [reason, setReason] = useState('');
    const [expiresAt, setExpiresAt] = useState('');
    const [error, setError] = useState<string | null>(null);

    React.useEffect(() => {
        if (open) {
            loadOrganizations();
        }
    }, [open]);

    const loadOrganizations = async () => {
        try {
            const orgs = await organizationApi.list();
            setOrganizations(orgs);
        } catch (err) {
            console.error('Error loading organizations:', err);
        }
    };

    const handleSubmit = async () => {
        setError(null);

        if (!selectedOrganization) {
            setError('Selecione uma organização');
            return;
        }

        if (!amount || parseInt(amount) <= 0) {
            setError('Informe uma quantidade válida de créditos');
            return;
        }

        if (!reason.trim()) {
            setError('Informe o motivo da adição de créditos');
            return;
        }

        setLoading(true);
        try {
            const request: AddExtraCreditsRequest = {
                organizationId: selectedOrganization.id,
                amount: parseInt(amount),
                reason: reason.trim(),
                expiresAt: expiresAt ? new Date(expiresAt).toISOString() : undefined,
            };

            await extraCreditsApi.addExtraCredits(request);
            toast.success('Créditos extras adicionados com sucesso!');

            // Reset form
            setSelectedOrganization(null);
            setAmount('');
            setReason('');
            setExpiresAt('');

            onSuccess?.();
            onClose();
        } catch (err: any) {
            setError(err?.message || 'Erro ao adicionar créditos extras');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            setSelectedOrganization(null);
            setAmount('');
            setReason('');
            setExpiresAt('');
            setError(null);
            onClose();
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AddIcon />
                    <Typography variant="h6">Adicionar Créditos Extras</Typography>
                </Box>
            </DialogTitle>

            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
                    <Autocomplete
                        options={organizations}
                        getOptionLabel={(option) => `${option.name} (${option.identifier})`}
                        value={selectedOrganization}
                        onChange={(_, value) => setSelectedOrganization(value)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Organização"
                                placeholder="Selecione uma organização"
                                required
                            />
                        )}
                        disabled={loading}
                    />

                    <TextField
                        label="Quantidade de Créditos"
                        type="number"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder="Ex: 1000"
                        required
                        disabled={loading}
                        inputProps={{ min: 1 }}
                        helperText="Quantidade de créditos extras a serem adicionados"
                    />

                    <TextField
                        label="Motivo"
                        multiline
                        rows={3}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Ex: Bônus promocional, Compensação por problema técnico, etc."
                        required
                        disabled={loading}
                        helperText="Descreva o motivo da adição de créditos extras"
                    />

                    <TextField
                        label="Data de Expiração (Opcional)"
                        type="datetime-local"
                        value={expiresAt}
                        onChange={(e) => setExpiresAt(e.target.value)}
                        disabled={loading}
                        InputLabelProps={{ shrink: true }}
                        helperText="Se não informado, os créditos não expiram"
                    />

                    <Alert severity="info" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                            <strong>Importante:</strong> Os créditos extras são adicionados ao saldo da organização
                            e não substituem os créditos mensais. Eles não expiram automaticamente e funcionam
                            como uma linha de crédito adicional.
                        </Typography>
                    </Alert>
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    Cancelar
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    sx={{ color: theme.palette.mode === 'dark' ? '#000' : undefined }}
                    disabled={loading || !selectedOrganization || !amount || !reason}
                    startIcon={loading ? <CircularProgress size={20} /> : <AddIcon />}
                >
                    {loading ? 'Adicionando...' : 'Adicionar Créditos'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddExtraCreditsModal;
